import { fromJS, Map } from "immutable";
import { normalizeDetailed } from "../../../../reduxFolder/reducers/outgoing";
import { normalizeDetailedShipment } from "../../../../reduxFolder/reducers/storage";
import {
  daysAgo,
  demoDetailedShipments,
  demoPicture,
} from "../../../Storage/components/DemoShipments/demoFixtures";
import demoShipment1Box from "../../../Storage/components/DemoShipments/images/demo-shipment1-box.jpeg";
import demoShipment1Label from "../../../Storage/components/DemoShipments/images/demo-shipment1-label.jpeg";
import demoShipment2Box from "../../../Storage/components/DemoShipments/images/demo-shipment2-box.jpeg";
import demoShipment2Label from "../../../Storage/components/DemoShipments/images/demo-shipment2-label.jpeg";
import demoShipment3Box from "../../../Storage/components/DemoShipments/images/demo-shipment3-box.jpeg";
import demoShipment3Label from "../../../Storage/components/DemoShipments/images/demo-shipment3-label.jpeg";

// Negative-id scheme (never collides with backend ids or the storage demo's
// -1/-2/-3, -1xx/-2xx/-3xx ranges): consolidations -501/-502 (packed) and
// -503 (pending), cart draft -601, consolidation item entries -7xx, extra
// nested shipments -51…-58, customs declarations -50xx, destination
// addresses -80x.

// One demo customer shipping to four different European destinations, so the
// sample parcel cards don't all repeat the same address. Deliberately fixed
// rather than randomized at runtime: the seeded detail caches and the demo
// cypress spec depend on stable fixtures.
//
// Shape note: the `street`/`house`/`flat` ("native") fields only apply to the
// countries in `supportedCountryPhoneMasks` (UA/BY/KG/KZ/RU) — see
// components/Addresses/addressFormHelper.ts. Everywhere else the real form
// stores `address_1`/`address_2`, which is what AddressItem falls back to, so
// these use that shape. None of them may carry an `id`: they are spread onto
// the consolidation details, where an `id` key would overwrite the
// consolidation's own id (address ids are passed separately as
// `destination_address_id`).
const DEMO_RECIPIENT = {
  first_name: "Alex",
  last_name: "Sabo",
};

const DRAFT_ADDRESS = {
  ...DEMO_RECIPIENT,
  address_1: "Kaiserstraße 12",
  address_2: "Wohnung 45",
  city: "Berlin",
  zip_code: "10115",
  country: "DE",
  phone: "+49 30 5550142",
};

const PACKED_1_ADDRESS = {
  ...DEMO_RECIPIENT,
  address_1: "24 Rue de Rivoli",
  address_2: "Appartement 7",
  city: "Paris",
  zip_code: "75001",
  country: "FR",
  phone: "+33 1 55 01 42 08",
};

const PACKED_2_ADDRESS = {
  ...DEMO_RECIPIENT,
  address_1: "Keizersgracht 210",
  city: "Amsterdam",
  zip_code: "1016 DW",
  country: "NL",
  phone: "+31 20 555 0142",
};

const PENDING_ADDRESS = {
  ...DEMO_RECIPIENT,
  address_1: "Calle de Alcalá 45",
  address_2: "3º B",
  city: "Madrid",
  zip_code: "28014",
  country: "ES",
  phone: "+34 91 555 0142",
};

// Inner parcels reuse the storage demo's three trackings (-1/-2/-3, seeded
// from demoDetailedShipments) plus extra realistic ones (-51…-58).
const EXTRA_TRACKINGS: Record<number, string> = {
  [-51]: "1Z999AA10123456784",
  [-52]: "9405511899560001234567",
  [-53]: "TBA308977122907",
  [-54]: "1Z5R89390304829571",
  [-55]: "9400111202555842761800",
  [-56]: "TBA317465529058",
  [-57]: "1Z8V42760391250041",
  [-58]: "9361289676091234567890",
};

const consolidationItem = (
  id: number,
  shipmentId: number,
  tracking: string,
) => ({
  id,
  shipment_id: shipmentId,
  description: tracking,
  shipment_tracking_number: tracking,
  emblem_thumb_url: null,
  service_requests: [],
  customer_comment: null,
  addons: [],
});

// Rows for the "Готово к оплате" section (shape = outgoing list bucket item;
// WaitingForPayment reads sku/carrier/total_weight/package_count/dims/estimate).
const rawDemoPaymentRows = [
  {
    id: -501,
    sku: "PK1027384",
    state: "packed",
    preferred_carrier: "FedEx International Economy",
    total_weight: 27.8,
    package_count: 1,
    width: 61,
    height: 48.3,
    depth: 50.8,
    estimate: { price: "486.00", total: "524.00" },
    excess_items: [],
    created_at: daysAgo(1),
    optional_line_item_codes: ["balanced"],
  },
  {
    id: -502,
    sku: "PK1027106",
    state: "packed",
    preferred_carrier: "East Cargo",
    total_weight: 30.6,
    package_count: 1,
    width: 55.9,
    height: 45.7,
    depth: 43.2,
    estimate: { price: "531.00", total: "568.00" },
    excess_items: [],
    created_at: daysAgo(3),
    optional_line_item_codes: ["balanced"],
  },
];

// Sum of the two estimates above, shown by the demo "Всего $…" header button.
export const demoPaymentTotal = 1092;

// Row for the "В работе" section: pending → blue "Ожидание упаковки" badge;
// InProgress shows only weight (brutto), no dims/price.
const rawDemoPackingRows = [
  {
    id: -503,
    sku: "PK1026771",
    state: "pending",
    preferred_carrier: "USPS Priority International",
    total_weight: 4.4,
    excess_items: [],
    created_at: daysAgo(0),
    optional_line_item_codes: ["balanced"],
  },
];

// Cart-draft row for "Не закончен". Never seeded into the redux cart slice
// (that would flip hasItems and leak into /shipping) — the row and
// DemoNotFinishedPanel read this fixture directly.
const rawDemoDraft = {
  id: -601,
  sku: "PK1027442",
  state: "requesting",
  delivery: "East Cargo",
  preferred_carrier: "East Cargo",
  weight: "6.11",
  created_at: daysAgo(0),
  optional_line_item_codes: ["balanced"],
  destination_address: { id: -801, ...DRAFT_ADDRESS },
  items: [
    consolidationItem(-751, -57, EXTRA_TRACKINGS[-57]),
    consolidationItem(-752, -58, EXTRA_TRACKINGS[-58]),
  ],
};

// Detailed consolidations (shape mirrors the /consolidations/:id response the
// reducer normalizes). Root `pictures` become packages[0] via normalizeDetailed
// and feed the photo strip — demo jpegs reused as placeholders, swappable
// exactly like the item photos. `consolidation_addons` stays empty because
// real accounts fetch real addon lists — empty avoids icon mismatches.
const rawDemoConsolidationDetails = [
  {
    ...rawDemoPaymentRows[0],
    ...PACKED_1_ADDRESS,
    destination_address_id: -802,
    weight: 27.8,
    pictures: [demoPicture(demoShipment1Box), demoPicture(demoShipment1Label)],
    consolidation_addons: [],
    customs_declarations: [
      {
        id: -5011,
        description: "Jig Saw",
        hs_code: "846722",
        quantity: 1,
        value: 179.99,
      },
      {
        id: -5012,
        description: "Sneakers",
        hs_code: "640419",
        quantity: 1,
        value: 109.99,
      },
      {
        id: -5013,
        description: "Wireless mouse",
        hs_code: "847160",
        quantity: 1,
        value: 79,
      },
      {
        id: -5016,
        description: "Camping tent",
        hs_code: "630622",
        quantity: 1,
        value: 189,
      },
      {
        id: -5017,
        description: "Coffee maker",
        hs_code: "851671",
        quantity: 1,
        value: 129,
      },
      {
        id: -5018,
        description: "Baby stroller",
        hs_code: "871500",
        quantity: 1,
        value: 349,
      },
    ],
    items: [
      consolidationItem(-701, -1, "532916641319"),
      consolidationItem(-702, -51, EXTRA_TRACKINGS[-51]),
      consolidationItem(-703, -52, EXTRA_TRACKINGS[-52]),
      consolidationItem(-704, -53, EXTRA_TRACKINGS[-53]),
    ],
    packages: [],
  },
  {
    ...rawDemoPaymentRows[1],
    ...PACKED_2_ADDRESS,
    destination_address_id: -803,
    weight: 30.6,
    pictures: [demoPicture(demoShipment2Box), demoPicture(demoShipment2Label)],
    consolidation_addons: [],
    customs_declarations: [
      {
        id: -5021,
        description: "Phone case",
        hs_code: "392690",
        quantity: 2,
        value: 12.5,
      },
      {
        id: -5022,
        description: "USB charging cable",
        hs_code: "854442",
        quantity: 1,
        value: 9.99,
      },
      {
        id: -5023,
        description: "Child car seat",
        hs_code: "940120",
        quantity: 1,
        value: 249,
      },
      {
        id: -5024,
        description: "Dumbbell set",
        hs_code: "950691",
        quantity: 1,
        value: 179,
      },
    ],
    items: [
      consolidationItem(-705, -2, "532092761354"),
      consolidationItem(-706, -54, EXTRA_TRACKINGS[-54]),
      consolidationItem(-707, -55, EXTRA_TRACKINGS[-55]),
    ],
    packages: [],
  },
  {
    ...rawDemoPackingRows[0],
    ...PENDING_ADDRESS,
    destination_address_id: -804,
    // Pending: not packed yet — no dims/photos, empty estimate.
    pictures: [],
    estimate: {},
    consolidation_addons: [],
    customs_declarations: [
      {
        id: -5031,
        description: "Ceramic mug",
        hs_code: "691200",
        quantity: 2,
        value: 18,
      },
      {
        id: -5032,
        description: "LED desk lamp",
        hs_code: "940520",
        quantity: 1,
        value: 34.9,
      },
      {
        id: -5033,
        description: "Portable speaker",
        hs_code: "851822",
        quantity: 1,
        value: 89,
      },
    ],
    items: [
      consolidationItem(-708, -3, "TBA332648215793"),
      consolidationItem(-709, -56, EXTRA_TRACKINGS[-56]),
    ],
    packages: [],
  },
];

// Details for the extra nested parcels (-51…-58) — same raw shape as the
// storage demo's rawDemoDetails, so StorageItemPanel renders them browse-only.
const extraShipmentDetail = (
  id: number,
  weight: number,
  receivedDaysAgo: number,
  pictures: Array<{ medium: string; large: string }>,
  declarations: Array<{
    id: number;
    description: string;
    hs_code: string;
    quantity: number;
    value: number;
  }>,
) => ({
  id,
  description: EXTRA_TRACKINGS[id],
  weight,
  received_at: daysAgo(receivedDaysAgo),
  state: "received",
  service_requests_count: [],
  items_quantity: 1,
  items: [
    {
      id: id * 10 - 1,
      weight,
      description: EXTRA_TRACKINGS[id],
      pictures: [],
      customs_declarations: declarations,
    },
  ],
  emblem_thumb_url: null,
  package_type: "box",
  prohibited: false,
  customer_comment: null,
  consolidation_id: null,
  addons: [],
  pictures,
  service_requests: [],
});

const pair1 = [demoPicture(demoShipment1Box), demoPicture(demoShipment1Label)];
const pair2 = [demoPicture(demoShipment2Box), demoPicture(demoShipment2Label)];
const pair3 = [demoPicture(demoShipment3Box), demoPicture(demoShipment3Label)];

const rawExtraShipmentDetails = [
  extraShipmentDetail(-51, 9.6, 12, pair1, [
    {
      id: -511,
      description: "Camping tent",
      hs_code: "630622",
      quantity: 1,
      value: 189,
    },
  ]),
  extraShipmentDetail(-52, 6.3, 11, pair2, [
    {
      id: -521,
      description: "Coffee maker",
      hs_code: "851671",
      quantity: 1,
      value: 129,
    },
  ]),
  extraShipmentDetail(-53, 7.2, 10, pair3, [
    {
      id: -531,
      description: "Baby stroller",
      hs_code: "871500",
      quantity: 1,
      value: 349,
    },
  ]),
  extraShipmentDetail(-54, 14.8, 8, pair1, [
    {
      id: -541,
      description: "Child car seat",
      hs_code: "940120",
      quantity: 1,
      value: 249,
    },
  ]),
  extraShipmentDetail(-55, 13.1, 7, pair2, [
    {
      id: -551,
      description: "Dumbbell set",
      hs_code: "950691",
      quantity: 1,
      value: 179,
    },
  ]),
  extraShipmentDetail(-56, 2.6, 4, pair3, [
    {
      id: -561,
      description: "Portable speaker",
      hs_code: "851822",
      quantity: 1,
      value: 89,
    },
  ]),
  extraShipmentDetail(-57, 2.31, 3, pair1, [
    {
      id: -571,
      description: "Denim jeans",
      hs_code: "620342",
      quantity: 2,
      value: 68,
    },
  ]),
  extraShipmentDetail(-58, 3.8, 2, pair2, [
    {
      id: -581,
      description: "Blender",
      hs_code: "850940",
      quantity: 1,
      value: 74.5,
    },
  ]),
];

// Same normalizers the reducers apply to real API responses, so the seeded
// caches match the post-reducer store shape exactly.
export const demoDetailedConsolidations = fromJS(
  rawDemoConsolidationDetails,
).map(normalizeDetailed);

export const demoOutgoingSeedPayload = Map({
  consolidations: demoDetailedConsolidations,
  shipments: demoDetailedShipments.concat(
    fromJS(rawExtraShipmentDetails).map(normalizeDetailedShipment),
  ),
});

export const demoPaymentItems = fromJS(rawDemoPaymentRows);
export const demoPackingItems = fromJS(rawDemoPackingRows);
export const demoDraftConsolidation = fromJS(rawDemoDraft);
